<template>
  <base-popover
    v-slot="{ close, open, opened, toggle }"
    ref="popoverRef"
    :class="brandClasses.popover"
    close-on-leave
    force-mount
    interactive
    :offset="0"
    strategy="fixed"
    @update:model-value="menuOpened = $event"
  >
    <div class="relative flex items-center">
      <component
        :is="getActionLinkComponent(content.target, { fallback: 'div' })"
        :class="[
          brandClasses.link,
          presets?.[content.menuStyle!]?.menu,
          { active: opened },
          { 'cursor-pointer bg-transparent p-0': isActionLinkOnly(content.target) },
        ]"
        v-bind="getActionLinkAttrs(content.target)"
        @click="emit('click')"
        @mouseenter="(e) => hasSubItem || menuHovered ? open(e) : undefined"
      >
        <span class="relative flex items-center gap-x-1 ws-nowrap">
          <vf-icon
            v-if="menuIcons[content.menuIcon?.icon!]"
            class="pointer-events-none"
            height="26"
            :src="menuIcons[content.menuIcon?.icon!]"
            width="26"
          />
          <span
            class="pointer-events-none"
            :class="[brandClasses.title, { 'sr-only': content.menuIcon && !content.titleVisibility }]"
            :style="[content.titleVisibility === 'title+icon' && { order: -1 }]"
          >
            <span v-if="$feature.showTitleHtml" v-html="content.titleHtml" />
            <template v-else>
              {{ content.title }}
            </template>
          </span>
          <span
            v-if="underlineOffset"
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 group-@hactive:scale-x-full scale-x-0 ease-out duration"
            :class="[
              brandClasses.underline,
              presets?.[content.menuStyle!]?.underline,
              { [brandClasses.underlineActive]: hasSubItem || suggestionsOpened },
            ]"
            :style="`margin-bottom: ${underlineOffset}; transform-origin: bottom left;`"
          />
        </span>
      </component>
      <base-button
        v-if="hasSubItem"
        :id="buttonId"
        :aria-controls="menuId"
        :aria-expanded="opened"
        class="absolute inset-y-0 right-0 w-0 overflow-hidden focus:w-3"
        @click="toggle"
        @keydown.esc="close"
      >
        <span class="sr-only">
          <span
            v-if="$feature.showTitleHtml"
            v-html="replaceAll($t.megaMenuToggle, { category: content.titleHtml })"
          />
          <template v-else>
            {{ replaceAll($t.megaMenuToggle, { category: content.title }) }}
          </template>
        </span>
        <vf-icon :dir="opened ? 'up' : 'down'" name="chevron" size="xs" />
      </base-button>
    </div>
    <lazy-base-popover-content
      :id="menuId"
      class="pb-5 pt-5 c-brand-1"
      :class="brandClasses.popoverContent"
      enter-active-class="transform ease-out"
      enter-from-class="op-0"
      leave-active-class="transform ease-in"
      leave-to-class="op-0"
      :max-height-offset="-1"
      @keydown.esc="close"
    >
      <div class="min-h-0 overflow-y-auto" :class="brandClasses.background">
        <div
          :aria-labelledby="buttonId"
          :class="brandClasses.menu"
          data-test-id="vf-mega-menu"
          role="menu"
          style="transform: translateY(-1px)"
        >
          <div
            class="grid mx-a max-w-360 gap-10"
            :style="{ gridTemplateColumns: visualSelectorCount === 1 ? '1fr 3fr' : undefined }"
          >
            <cms-section
              v-if="content.visualSelectors"
              :section="{ items: content.visualSelectors, name: 'visual-selectors' }"
            />
            <div class="mx-a max-w-256">
              <vf-link
                v-if="!content.hideShopAll?.lg && content.target?.url"
                :class="brandClasses.shopAll"
                :target="content.target.targetAttribute"
                :to="content.target.url"
                variant="quiet"
                @click="$emit('click')"
              >
                {{ content.overrideShopAll || `${$t.megaMenuShopAll} ${content.title}` }}
              </vf-link>
              <div :class="[brandClasses.columns, columnsGap]">
                <!-- Featured Column -->
                <template v-if="hasFeaturedColumn">
                  <div
                    class="f-col items-start"
                    :class="hasVisualSelectors ? 'gap-5' : 'gap-9'"
                    style="flex-basis: 168%; max-width: 20.875rem"
                  >
                    <cms-section
                      v-for="menuItem in filteredFeaturedColumn"
                      :key="menuItem.id"
                      :section="{ name: menuItem.id, items: [menuItem] }"
                    />
                  </div>
                  <div aria-hidden="true">
                    <div class="h-full b-transparent bg-brand-7" style="width: 1px" />
                  </div>
                </template>

                <!-- Columns -->
                <template
                  v-for="({ children }, i) in filteredColumns"
                  :key="i"
                >
                  <div
                    v-if="i !== 3 || children"
                    :class="brandClasses.column"
                    style="flex-basis: 100%; max-width: 12.5rem; min-width:9.375rem"
                  >
                    <cms-section
                      v-for="menuItem in children"
                      :key="menuItem.id"
                      :section="{ name: menuItem.id, items: [menuItem] }"
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </lazy-base-popover-content>
  </base-popover>
</template>

<script lang="ts" setup>
import type { BasePopover } from '#components'
import type { MenuItem } from '#types/components/cms/mega-menu'
import { HeaderContextKey } from '#commerce/components/vf/header/context'
import { BaseButton } from '#components'
import { MegaMenuL2ContextKey, MegaMenuVisualSelectorContextKey } from '#content/contexts/megamenu'

export type Props = {
  content: MenuItem
}

export type Emits = {
  click: []
}

const { content } = defineProps<Props>()

const emit = defineEmits<Emits>()

const { getActionLinkAttrs, getActionLinkComponent, isActionLinkOnly } = useCmsActionLink()
const config = useAppConfig()
const { menuIcons } = config.assets
const { brandClasses, presets, underlineOffset } = config.components.cms.megaMenuL1Desktop
const router = useRouter()

const popoverRef = ref<InstanceType<typeof BasePopover>>()

const buttonId = `${content.id}-button`
const menuId = `${content.id}-menu`

const { menuHovered, menuOpened, suggestionsOpened } = inject(HeaderContextKey)!

const filteredColumns = computed(
  () => content.columns?.map((column) => ({
    ...column,
    children: column.children?.filter(({ hideOnWeb }) => !hideOnWeb),
  })) ?? [],
)
const visualSelectorCount = ref(content.visualSelectors?.length ?? 0)
const columnsGap = computed(() => filteredColumns.value.filter((column) => column.children).length > 3 ? 'gap-12' : 'gap-16')

const filteredFeaturedColumn = computed(() => content.featuredColumn?.filter(({ hideOnWeb }) => !hideOnWeb) ?? [])
const hasFeaturedColumn = computed(() => !!filteredFeaturedColumn.value.length)
const hasVisualSelectors = computed(() => !!visualSelectorCount.value)
const hasSubItem = computed(() => filteredColumns.value.some((column) => Object.keys(column).length)
  || content.menuBanner
  || hasFeaturedColumn.value
  || hasVisualSelectors.value)

// Provide context for the featured column area so that VfCanvasMegaMenuL3 items receive correct styling
// MegaMenuL2 components in regular columns override this with their own provide
provide(MegaMenuL2ContextKey, { featured: true, title: content.title, visualSelectors: hasVisualSelectors })
provide(MegaMenuVisualSelectorContextKey, visualSelectorCount)

const unregisteredRouter = router.beforeEach(() => {
  if (popoverRef.value?.opened) popoverRef.value.close()
})
onUnmounted(() => {
  unregisteredRouter()
})
</script>
